import { UserProfileForAdmin } from '@infraloop/services'

/** Direction the subscription-end-date column is sorted in. */
export enum SortDirection {
  Asc = 'asc',
  Desc = 'desc',
}

/** Sort state of the subscription-end-date column; `undefined` means unsorted. */
export type SortState = SortDirection | undefined

/**
 * Step the subscription-end sort to its next state, cycling
 * descending -> ascending -> unsorted.
 * @param state Current sort state.
 */
export const cycleSortState = (state: SortState): SortState => {
  if (state === SortDirection.Desc) return SortDirection.Asc
  if (state === SortDirection.Asc) return undefined
  return SortDirection.Desc
}

/** Avatar palette, mirroring the mockup — a colour is picked deterministically per user. */
const AVATAR_PALETTE = ['#2699FB', '#1B2A4E', '#2285DA', '#5C7AAE', '#3E8E7E', '#8A6FB0']

/**
 * Pick a stable avatar background colour for a user from a seed (their email).
 * @param seed Stable per-user string.
 */
export const getAvatarColor = (seed: string): string => {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % AVATAR_PALETTE.length
  }
  return AVATAR_PALETTE[hash]
}

/** The user's full name from HubSpot properties (`姓 名`), or an empty string. */
export const getUserFullName = (user: UserProfileForAdmin): string =>
  `${user.hubspot_properties?.last_name ?? ''} ${user.hubspot_properties?.first_name ?? ''}`.trim()

/**
 * Secondary identity line under the user's email: name · company · department, skipping
 * blanks. Empty when nothing is known.
 * @param user The user row.
 */
export const getUserMetaLine = (user: UserProfileForAdmin): string =>
  [getUserFullName(user), user.hubspot_properties?.company, user.hubspot_properties?.department]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' · ')

/** Stripe customer page for a customer id. */
export const buildStripeCustomerUrl = (stripeId: string): string => `https://dashboard.stripe.com/customers/${stripeId}`

/** In-app dashboard link for a user (admins look people up from here). */
export const buildUserDashboardUrl = (userId: string): string => `/?user_id=${encodeURIComponent(userId)}`
